import { blue, red, green, orange, grey, common, deepPurple } from '@mui/material/colors'
import { styled } from '@mui/material/styles'
import { PickersDay } from '@mui/x-date-pickers/PickersDay'

export const BILLING_STATUS_COLORS = {
  EMPTY: 'transparent',
  UNBILLED: orange[100],
  BILLED: green[100],
  GRACE_PERIOD: blue[100],
  RENTAL_PERIOD: 'transparent',
}

export const RENTAL_PERIOD_BORDER_COLOR = blue[300]

export const SELECTION_COLORS = {
  START_DATE: blue[500],
  END_DATE: red[500],
  IN_RANGE: blue[50],
  TODAY: blue[100],
  PENDING_SELECTION: blue[200],
}

export const EVENT_BADGE_COLOR = deepPurple[500]

const getBillingStatusBackground = billingStatus => BILLING_STATUS_COLORS[billingStatus] || BILLING_STATUS_COLORS.EMPTY

const getRentalPeriodBothBoundaryStyles = (isRentalPeriodStartDate, isRentalPeriodEndDate, isStartDate, isEndDate, isStartEndSame) => {
  if (isRentalPeriodStartDate && isRentalPeriodEndDate && !isStartDate && !isEndDate && !isStartEndSame) {
    return {
      background: `linear-gradient(90deg, ${red[200]} 50%, ${blue[200]} 50%)`,
      borderRadius: '0 !important',
    }
  }
  return {}
}

/**
 * Styled PickersDay component for rental fee calendar
 * Supports billing status colors and range selection indicators
 */
export const StyledRentalFeeDay = styled(PickersDay)(
  ({
    theme,
    billingStatus,
    isStartDate,
    isEndDate,
    isInRange,
    isToday,
    isStartEndSame,
    isRentalPeriodStartDate,
    isRentalPeriodEndDate,
    isPendingSelection,
    isWithinClosedPeriod,
  }) => {
    const backgroundColor = getBillingStatusBackground(billingStatus)

    const baseStyles = {
      borderRadius: 0,
      width: '100%',
      height: 'auto',
      aspectRatio: '1 / 1',
      margin: 0,
      fontSize: '0.75rem',
      fontWeight: 500,
      position: 'relative',
      backgroundColor,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        opacity: 0.9,
      },
      '&.Mui-selected': {
        backgroundColor: blue[500],
        color: common.white,
        '&:hover': {
          backgroundColor: blue[600],
        },
      },
    }

    const todayStyles = isToday
      ? {
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: `1px solid ${blue[500]}`,
            borderRadius: '50%',
          },
        }
      : {}

    const startDateStyles =
      isStartDate && !isStartEndSame
        ? {
            backgroundColor: SELECTION_COLORS.START_DATE,
            color: common.white,
            borderRadius: 0,
            borderTopLeftRadius: '16px',
            borderBottomLeftRadius: '16px',
            '&:hover': {
              backgroundColor: blue[600],
            },
          }
        : {}

    const endDateStyles =
      isEndDate && !isStartEndSame
        ? {
            backgroundColor: SELECTION_COLORS.END_DATE,
            color: common.white,
            borderRadius: 0,
            borderTopRightRadius: '16px',
            borderBottomRightRadius: '16px',
            '&:hover': {
              backgroundColor: red[600],
            },
          }
        : {}

    const startEndSameStyles = isStartEndSame
      ? {
          background: `linear-gradient(90deg, ${SELECTION_COLORS.END_DATE} 50%, ${SELECTION_COLORS.START_DATE} 50%)`,
          color: common.white,
          '&:hover': {
            opacity: 0.9,
          },
        }
      : {}

    const inRangeStyles =
      isInRange && !isStartDate && !isEndDate
        ? {
            backgroundColor,
            borderRadius: 0,
          }
        : {}

    const rentalPeriodStartStyles =
      isRentalPeriodStartDate && !isStartDate && !isStartEndSame
        ? {
            backgroundColor: blue[200],
            borderRadius: 0,
            borderTopLeftRadius: '16px',
            borderBottomLeftRadius: '16px',
          }
        : {}

    const rentalPeriodEndStyles =
      isRentalPeriodEndDate && !isEndDate && !isStartEndSame
        ? {
            backgroundColor: red[200],
            borderRadius: 0,
            borderTopRightRadius: '16px',
            borderBottomRightRadius: '16px',
          }
        : {}

    const rentalPeriodStartEndSameStyles = getRentalPeriodBothBoundaryStyles(
      isRentalPeriodStartDate,
      isRentalPeriodEndDate,
      isStartDate,
      isEndDate,
      isStartEndSame
    )

    const pendingSelectionStyles = isPendingSelection
      ? {
          backgroundColor: SELECTION_COLORS.PENDING_SELECTION,
          border: `2px dashed ${blue[500]}`,
          borderRadius: '50%',
          '&:hover': {
            backgroundColor: blue[300],
          },
        }
      : {}

    const closedPeriodStyles = isWithinClosedPeriod
      ? {
          opacity: 0.85,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }
      : {}

    return {
      ...baseStyles,
      ...inRangeStyles,
      ...rentalPeriodStartStyles,
      ...rentalPeriodEndStyles,
      ...rentalPeriodStartEndSameStyles,
      ...startDateStyles,
      ...endDateStyles,
      ...startEndSameStyles,
      ...closedPeriodStyles,
      ...pendingSelectionStyles,
      ...todayStyles,
    }
  }
)

/**
 * Base calendar container styles (shared between fixed and flexible layouts)
 */
const baseCalendarStyles = {
  width: '100%',
  '& .MuiPickersCalendarHeader-root': {
    paddingLeft: 2,
    paddingRight: 2,
  },
  '& .MuiDayCalendar-header': {
    '& .MuiTypography-root': {
      fontSize: '0.75rem',
      fontWeight: 700,
    },
  },
  '& .MuiPickersCalendarHeader-label': {
    fontSize: '0.875rem',
    fontWeight: 700,
  },
  '& .MuiDayCalendar-weekDayLabel': {
    width: 'calc(100% / 7)',
  },
  '& .MuiDateRangeCalendar-monthContainer': {
    width: '100%',
  },
}

/**
 * Calendar container styles with fixed height (for fixed-width contexts like dialogs)
 */
export const calendarContainerStyles = {
  ...baseCalendarStyles,
  '& .MuiDayCalendar-slideTransition': {
    minHeight: '373px',
  },
  '& .MuiPickersSlideTransition-root': {
    minHeight: '373px',
  },
}

/**
 * Calendar container styles with flexible height (for dynamic-width contexts like accordions)
 * Height is calculated based on aspect ratio to accommodate 6 week rows plus weekday header
 */
export const getFlexibleCalendarStyles = () => ({
  ...baseCalendarStyles,
  '& .MuiDayCalendar-slideTransition': {
    minHeight: 'auto',
    aspectRatio: '6.5 / 6',
  },
  '& .MuiPickersSlideTransition-root': {
    minHeight: 'auto',
    aspectRatio: '6.5 / 6',
  },
})

/**
 * Legend item styles
 */
export const legendItemStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

export const legendColorBoxStyles = (color, isSplit = false, splitColor = null) => ({
  width: 16,
  height: 16,
  borderRadius: '2px',
  border: '1px solid',
  borderColor: 'divider',
  ...(isSplit
    ? {
        background: `linear-gradient(90deg, ${color} 50%, ${splitColor} 50%)`,
      }
    : {
        backgroundColor: color,
      }),
})

/**
 * Chip styles for period states
 */
export const closedChipStyles = {
  borderColor: grey[400],
  color: grey[600],
  '& .MuiChip-icon': { color: grey[500] },
  opacity: 0.8,
}

export const ongoingChipStyles = {
  borderColor: blue[500],
  color: blue[500],
}

export const addEndDateChipStyles = {
  borderColor: orange[500],
  color: orange[500],
  borderStyle: 'dashed',
}
